import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import AtlasDraftLogo from '@/assets/atlasdraft-logo-dark.png';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { usePlatformSettingsPublic } from '@/hooks/usePlatformSettingsPublic';
import { TOAST } from '@/lib/toastMessages';
import { z } from 'zod';

// Login schema - more lenient for existing users
const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  displayName: z.string().optional(),
});

// Signup schema - stronger requirements for new users
const signupSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-zA-Z]/, 'Password must contain at least one letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  displayName: z.string().optional(),
});

// Password strength calculator
const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
  if (!password) return { strength: 0, label: '', color: '' };
  
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-zA-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++; // Bonus for special chars
  
  if (score <= 2) return { strength: 33, label: 'Weak', color: 'bg-destructive' };
  if (score <= 3) return { strength: 66, label: 'Fair', color: 'bg-amber-500' };
  return { strength: 100, label: 'Strong', color: 'bg-green-500' };
};

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  
  const { signIn, signUp, resetPassword } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { settings, isLoading: settingsLoading } = usePlatformSettingsPublic();

  // If registration is disabled, force login mode
  useEffect(() => {
    if (!settings.registration_enabled && !isLogin) {
      setIsLogin(true);
    }
  }, [settings.registration_enabled, isLogin]);

  // Calculate password strength (only relevant for signup)
  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);

  const validateForm = () => {
    try {
      // Use different schemas for login vs signup
      const schema = isLogin ? loginSchema : signupSchema;
      schema.parse({ email, password, displayName });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: { email?: string; password?: string } = {};
        error.errors.forEach((err) => {
          if (err.path[0] === 'email') fieldErrors.email = err.message;
          if (err.path[0] === 'password') fieldErrors.password = err.message;
        });
        setErrors(fieldErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Double-check registration is enabled for signups
    if (!isLogin && !settings.registration_enabled) {
      toast({ ...TOAST.ACCOUNT.REGISTRATION_DISABLED, variant: 'destructive' });
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) {
          toast({
            title: 'Login failed',
            description: error.message.includes('Invalid login credentials') 
              ? 'Invalid email or password. Please try again.' 
              : error.message,
            variant: 'destructive',
          });
        } else {
          toast(TOAST.AUTH.LOGIN_SUCCESS);
          navigate('/projects');
        }
      } else {
        const { error } = await signUp(email, password, displayName);
        if (error) {
          if (error.message.includes('already registered')) {
            toast({ ...TOAST.ACCOUNT.ACCOUNT_EXISTS, variant: 'destructive' });
          } else {
            toast({
              title: 'Sign up failed',
              description: error.message,
              variant: 'destructive',
            });
          }
        } else {
          toast(TOAST.ACCOUNT.WELCOME);
          navigate('/projects');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const emailResult = z.string().email('Please enter a valid email address').safeParse(email);
    if (!emailResult.success) {
      setErrors({ email: emailResult.error.errors[0].message });
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const { error } = await resetPassword(email);
      if (error) {
        toast({ ...TOAST.ACCOUNT.RESET_ERROR, variant: 'destructive' });
      } else {
        toast(TOAST.ACCOUNT.RESET_SUCCESS);
        setIsForgotPassword(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-surface-elevated p-12 flex-col justify-between">
        <div className="flex items-center">
          <img src={AtlasDraftLogo} alt="AtlasDraft" className="h-10" />
        </div>
        
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-3xl font-display font-bold text-foreground leading-tight">
              Write your book with<br />
              <span className="text-primary">AI-powered assistance</span>
            </h2>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-muted-foreground max-w-md"
          >
            From concept to manuscript, {settings.platform_name} guides you through every step of the writing process with intelligent research, outlining, and drafting tools.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex gap-8 text-sm"
          >
            <div>
              <div className="text-2xl font-bold text-foreground">3</div>
              <div className="text-muted-foreground">Writing Modes</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">AI</div>
              <div className="text-muted-foreground">Research Assistant</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">∞</div>
              <div className="text-muted-foreground">Possibilities</div>
            </div>
          </motion.div>
        </div>
        
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} {settings.platform_name}. All rights reserved.
        </p>
      </div>
      
      {/* Right side - Auth form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md space-y-8"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src={AtlasDraftLogo} alt="AtlasDraft" className="h-10" />
          </div>
          
          <div className="text-center">
            <h2 className="text-2xl font-display font-bold text-foreground">
              {isForgotPassword 
                ? 'Reset your password' 
                : isLogin 
                  ? 'Welcome back' 
                  : 'Create your account'}
            </h2>
            <p className="text-muted-foreground mt-2">
              {isForgotPassword
                ? "Enter your email and we'll send you a reset link"
                : isLogin 
                  ? 'Enter your credentials to access your projects' 
                  : 'Start your writing journey today'}
            </p>
          </div>

          {/* Registration disabled notice */}
          {!settings.registration_enabled && !isLogin && !isForgotPassword && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                New account registration is currently closed. Please contact support if you need access.
              </AlertDescription>
            </Alert>
          )}

          {/* Forgot Password Form */}
          {isForgotPassword ? (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
                    required
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Send Reset Link
              </Button>
              
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(false);
                    setErrors({});
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Your name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="pl-10"
                    disabled={!settings.registration_enabled}
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
                  required
                />
              </div>
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setErrors({});
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pl-10 ${errors.password ? 'border-destructive' : ''}`}
                  required
                />
              </div>
              {/* Password strength indicator - only show during signup */}
              {!isLogin && password && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Progress 
                      value={passwordStrength.strength} 
                      className="h-1.5 flex-1"
                      indicatorClassName={
                        passwordStrength.label === 'Weak' ? 'bg-destructive' :
                        passwordStrength.label === 'Fair' ? 'bg-amber-500' :
                        'bg-green-500'
                      }
                    />
                    <span className={`text-xs font-medium ${
                      passwordStrength.label === 'Weak' ? 'text-destructive' :
                      passwordStrength.label === 'Fair' ? 'text-amber-500' :
                      'text-green-500'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use 8+ characters with letters and numbers
                  </p>
                </div>
              )}
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || settingsLoading || (!isLogin && !settings.registration_enabled)}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              {isLogin ? 'Sign In' : 'Create Account'}
            </Button>
          </form>
          )}
          
          {!isForgotPassword && (
          <div className="text-center">
            {settings.registration_enabled ? (
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setErrors({});
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {isLogin 
                  ? "Don't have an account? Sign up" 
                  : 'Already have an account? Sign in'}
              </button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Registration is currently closed.{' '}
                {settings.support_email && (
                  <a 
                    href={`mailto:${settings.support_email}`}
                    className="text-primary hover:underline"
                  >
                    Contact support
                  </a>
                )}
              </p>
            )}
          </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
